import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

const PrivacyPolicyPage = () => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="privacy-policy-page">
    <Helmet>
      <title>Privacy Policy | Common Impact E-Learning</title>
      <meta name="description" content="Privacy Policy for Common Impact E-Learning. How we collect, use, and protect your personal data under UK GDPR." />
    </Helmet>
    <Header />
    <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
      <h1 className="text-3xl sm:text-4xl font-bold text-[#2a3e5d] mb-2">Privacy Policy</h1>
      <p className="text-sm text-slate-500 mb-8">Last updated: 1 June 2026</p>

      <div className="prose prose-slate max-w-none prose-headings:text-[#2a3e5d] prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h3:text-lg prose-p:text-sm prose-p:leading-relaxed prose-li:text-sm">
        <h2>1. Introduction</h2>
        <p>Common Impact E-Learning ("we", "us", "our") is committed to protecting and respecting your privacy. This policy explains how we collect, use, store, and share your personal data when you use our platform at commonimpactelearning.co.uk, in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>

        <h2>2. Data Controller</h2>
        <p>The data controller responsible for your personal data is:</p>
        <p><strong>Common Impact E-Learning</strong><br />71-75 Shelton Street, London, WC2H 9JQ, England<br />Email: <a href="mailto:admin@commonimpactelearning.co.uk" className="text-[#20acf6]">admin@commonimpactelearning.co.uk</a></p>

        <h2>3. What Data We Collect</h2>
        <p>We collect the following categories of personal data:</p>
        <h3>3.1 Account Information</h3>
        <ul>
          <li>Full name, email address, phone number (optional)</li>
          <li>Organisation name and role (for organisation accounts)</li>
          <li>Password (stored securely using bcrypt hashing)</li>
        </ul>
        <h3>3.2 Learning Data</h3>
        <ul>
          <li>Course enrolment and completion records</li>
          <li>Quiz and assessment results</li>
          <li>Progress tracking data (video watch time, scroll depth, interaction completion)</li>
          <li>Certificates issued and verification records</li>
          <li>Reflection journal entries and case study responses</li>
        </ul>
        <h3>3.3 Payment Data</h3>
        <ul>
          <li>Billing name and email</li>
          <li>Payment transaction records (card details are processed by Stripe and never stored on our servers)</li>
          <li>Subscription status and history</li>
        </ul>
        <h3>3.4 Technical Data</h3>
        <ul>
          <li>IP address, browser type, device information</li>
          <li>Pages visited, timestamps, and session data</li>
          <li>Cookies and similar technologies (see our Cookies Policy)</li>
        </ul>

        <h2>4. Why We Collect Your Data</h2>
        <p>We process your personal data for the following purposes:</p>
        <ul>
          <li><strong>To provide the platform:</strong> Creating accounts, delivering courses, tracking progress, issuing certificates</li>
          <li><strong>To process payments:</strong> Handling subscriptions and one-time purchases via Stripe</li>
          <li><strong>To communicate with you:</strong> Sending course reminders, compliance alerts, support responses, and service notifications</li>
          <li><strong>To comply with regulations:</strong> Maintaining audit logs for CQC compliance, generating mandatory training reports</li>
          <li><strong>To improve our service:</strong> Analysing usage patterns to improve course content and platform features</li>
        </ul>
        <p>Our lawful bases for processing are: performance of a contract (providing the platform service), legitimate interests (improving services, security), compliance with legal obligations, and your consent (where applicable, e.g., marketing communications).</p>

        <h2>5. Who We Share Data With</h2>
        <p>We share personal data with the following third-party providers, only as necessary to deliver our services:</p>
        <table className="text-sm">
          <thead>
            <tr><th className="text-left p-2 bg-slate-50">Provider</th><th className="text-left p-2 bg-slate-50">Purpose</th><th className="text-left p-2 bg-slate-50">Data Shared</th></tr>
          </thead>
          <tbody>
            <tr><td className="p-2 border-t">Stripe</td><td className="p-2 border-t">Payment processing</td><td className="p-2 border-t">Name, email, payment details</td></tr>
            <tr><td className="p-2 border-t">SendGrid (Twilio)</td><td className="p-2 border-t">Sending transactional emails</td><td className="p-2 border-t">Name, email address</td></tr>
            <tr><td className="p-2 border-t">MongoDB Atlas</td><td className="p-2 border-t">Database hosting</td><td className="p-2 border-t">All platform data (encrypted at rest)</td></tr>
            <tr><td className="p-2 border-t">Cloudinary</td><td className="p-2 border-t">Image and file storage</td><td className="p-2 border-t">Uploaded media files</td></tr>
          </tbody>
        </table>
        <p>All third-party providers are contractually required to process data in accordance with UK GDPR. We do not sell your personal data to any third party.</p>

        <h2>6. Data Retention</h2>
        <ul>
          <li><strong>Account data:</strong> Retained for as long as your account is active, plus 12 months after account deletion to fulfil legal obligations</li>
          <li><strong>Learning records & certificates:</strong> Retained for 6 years (to support CQC compliance and professional development records)</li>
          <li><strong>Payment records:</strong> Retained for 7 years (as required by HMRC)</li>
          <li><strong>Technical/audit logs:</strong> Retained for 24 months</li>
          <li><strong>Contact enquiries:</strong> Retained for 12 months</li>
        </ul>

        <h2>7. Your Rights Under UK GDPR</h2>
        <p>You have the following rights regarding your personal data:</p>
        <ul>
          <li><strong>Right of access:</strong> Request a copy of your personal data</li>
          <li><strong>Right to rectification:</strong> Request correction of inaccurate data</li>
          <li><strong>Right to erasure:</strong> Request deletion of your data (subject to legal retention obligations)</li>
          <li><strong>Right to restrict processing:</strong> Request limitation of how we process your data</li>
          <li><strong>Right to object:</strong> Object to processing based on legitimate interests or direct marketing</li>
          <li><strong>Right to data portability:</strong> Receive your data in a structured, machine-readable format</li>
          <li><strong>Right to withdraw consent:</strong> Where processing is based on consent, you may withdraw it at any time</li>
        </ul>
        <p>To exercise any of these rights, contact us at <a href="mailto:admin@commonimpactelearning.co.uk" className="text-[#20acf6]">admin@commonimpactelearning.co.uk</a>. We will respond within 30 days. You also have the GDPR data export and account deletion features available in your account settings.</p>

        <h2>8. Data Security</h2>
        <p>We implement appropriate technical and organisational measures to protect your data, including:</p>
        <ul>
          <li>Encryption of data in transit (TLS/SSL) and at rest</li>
          <li>Secure password hashing (bcrypt)</li>
          <li>JWT-based authentication with token expiry</li>
          <li>Role-based access controls</li>
          <li>Regular audit logging of data access</li>
        </ul>

        <h2>9. International Transfers</h2>
        <p>Some of our third-party providers may process data outside the UK. Where this occurs, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs) or adequacy decisions.</p>

        <h2>10. Children's Data</h2>
        <p>Our platform is not intended for use by children under 16. We do not knowingly collect data from children. If we become aware that a child's data has been collected, we will delete it promptly.</p>

        <h2>11. Complaints</h2>
        <p>If you are not satisfied with how we handle your personal data, you have the right to lodge a complaint with the Information Commissioner's Office (ICO):</p>
        <p>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#20acf6]">ico.org.uk</a><br />Helpline: 0303 123 1113</p>

        <h2>12. Changes to This Policy</h2>
        <p>We may update this policy from time to time. The "Last updated" date at the top will reflect the most recent version. We will notify users of material changes via email or platform notification.</p>
      </div>
    </main>
    <Footer />
  </div>
);

export default PrivacyPolicyPage;
